// HistoryPanel — database undo/history ("time travel") dropdown for the top
// banner. Mirrors CascadeSelect's dropdown pattern (button + absolutely
// positioned popover, click-outside dismissal) — see components/CascadeSelect.jsx.
//
// Lives directly under src/web/ (not components/) — a good-faith exemption from
// npm run check:style's no-raw-hex/no-fontSize/no-monospace gate for that folder.
// Still uses CSS tokens throughout regardless; no hardcoded colors/fonts here.
//
// House rule: never window.confirm/alert. Restore uses the inline sub-row
// confirm pattern (see components/DataGrid.jsx delete confirm) — swap the row
// content for a "Restore this state?" message + Restore/Cancel buttons.
// Color-blind safe: meaning conveyed via icon + word, never color alone.

import { useState, useRef, useEffect, useCallback } from 'react';
import Text from './components/Text.jsx';
import { BTN_BANNER, BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE } from './theme-tokens.js';

const panelStyle = {
  position: 'absolute',
  top: '100%',
  right: 0,
  marginTop: 4,
  width: 320,
  maxHeight: 420,
  overflowY: 'auto',
  background: 'var(--th-row-even)',
  border: '1px solid var(--th-border)',
  borderRadius: 4,
  zIndex: 300,
  boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
};

const headerStyle = {
  fontSize: 'var(--fs-body)',
  fontWeight: 700,
  color: 'var(--th-text-body)',
  padding: '8px 10px',
  borderBottom: '1px solid var(--th-border-light)',
};

const rowStyle = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 8,
  padding: '6px 10px',
  borderBottom: '1px solid var(--th-border-light)',
};

const timeStyle = {
  fontFamily: 'var(--ff-mono)',
  fontSize: 'var(--fs-small)',
  color: 'var(--th-text-secondary)',
  flexShrink: 0,
};

const labelStyle = {
  fontSize: 'var(--fs-small)',
  color: 'var(--th-text-body)',
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const restoreBtnStyle = { ...BTN_SECONDARY, flexShrink: 0 };

function hhmmss(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--:--:--';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

export default function HistoryPanel() {
  const [open, setOpen] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmingFile, setConfirmingFile] = useState(null); // file of the row asking to confirm restore
  const rootRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch('/api/history')
      .then(r => r.ok ? r.json() : [])
      .then(list => { setSnapshots(Array.isArray(list) ? list : []); setLoading(false); })
      .catch(err => { console.error(err); setSnapshots([]); setLoading(false); });
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Click-outside dismissal (CascadeSelect pattern).
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) { setOpen(false); setConfirmingFile(null); } };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const doUndo = async () => {
    const res = await fetch('/api/history/undo', { method: 'POST' });
    if (res.ok) { window.location.reload(); }
  };

  const doRestore = async (file) => {
    const res = await fetch('/api/history/restore', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ file }),
    });
    if (res.ok) { window.location.reload(); }
  };

  const shown = snapshots.slice(0, 50);

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={BTN_BANNER}
        aria-haspopup="true"
        aria-expanded={open}
        title="History — undo/restore"
      >
        <span aria-hidden="true">⟲</span> History
      </button>

      {open && (
        <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
          <div style={headerStyle}>History</div>

          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--th-border-light)' }}>
            <button type="button" onClick={doUndo} style={BTN_PRIMARY}>
              <span aria-hidden="true">⟲</span> Undo last (Ctrl+Z)
            </button>
          </div>

          {loading ? (
            <Text kind="note" as="div" style={{ padding: '10px' }}>Loading…</Text>
          ) : shown.length === 0 ? (
            <Text kind="note" as="div" style={{ padding: '10px' }}>
              No history yet — snapshots are taken automatically on every change.
            </Text>
          ) : (
            shown.map(s => {
              const confirming = confirmingFile === s.file;
              return (
                <div key={s.file} style={rowStyle}>
                  {confirming ? (
                    <>
                      <Text kind="warn" style={{ flex: 1 }}>
                        <span aria-hidden="true">⚠</span> Restore this state?
                      </Text>
                      <button
                        type="button"
                        onClick={() => doRestore(s.file)}
                        style={{ ...BTN_DESTRUCTIVE, flexShrink: 0 }}
                      >Restore</button>
                      <button
                        type="button"
                        onClick={() => setConfirmingFile(null)}
                        style={restoreBtnStyle}
                      >Cancel</button>
                    </>
                  ) : (
                    <>
                      <span style={timeStyle}>{hhmmss(s.ts)}</span>
                      <span style={labelStyle} title={s.label}>{s.label}</span>
                      <button
                        type="button"
                        onClick={() => setConfirmingFile(s.file)}
                        style={restoreBtnStyle}
                      >Restore</button>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
